
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useFontSize } from '../../context/FontSizeContext';
import { LoginModal } from './LoginModal';
import { useAuth, redirectToUserMainPage } from '../../context/AuthContext';
import { usePortal } from '../../pages/home/PortalContext';
import { getUdaThemeById } from '../../pages/home/udaThemes';
import axios from 'axios';

/* ------------------------------------------------------------------ */
/*  Header leader photos (fetched from API, global across all UDAs)   */
/* ------------------------------------------------------------------ */
const fallbackHeaderLeaders = [
  { ldr_nm_tx: 'Sri Nara Chandrababu Naidu', dsgntn_tx: "Hon'ble Chief Minister of AP", pht_url_tx: 'https://cdma.ap.gov.in/assets/profile-pics/chandrababu.png' },
  { ldr_nm_tx: 'Sri Ponguru Narayana', dsgntn_tx: "Hon'ble Minister for MA&UD", pht_url_tx: 'https://cdma.ap.gov.in/assets/profile-pics/narayana3.png' },
];

/* ------------------------------------------------------------------ */
/*  Navigation configuration                                          */
/* ------------------------------------------------------------------ */
interface SubItem { label: string; link: string }
interface NavItem { label: string; link: string; children?: SubItem[] }

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', link: '/' },
  {
    label: 'About Us',
    link: '/about',
    children: [
      { label: 'Functions', link: '/about/functions' },
      { label: 'Citizen Charter', link: '/about/citizen-charter' },
      { label: 'Team', link: '/about/team' },
    ],
  },
  { label: 'Services', link: '/services' },
  {
    label: 'Resources',
    link: '/resources',
    children: [
      { label: 'Acts & By-Laws', link: '/resources/acts-bylaws' },
      { label: 'GOs & Circulars', link: '/resources/gos-circulars' },
      { label: 'FAQs', link: '/resources/faqs' },
      { label: 'Training Materials', link: '/resources/training' },
    ],
  },
  { label: 'GIS Map', link: '/gis-map' },
  { label: 'RTI', link: '/rti' },
  { label: 'Contact Us', link: '/contact' },
  { label: 'Help Desk', link: '/help-desk' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export const Header: React.FC = () => {
  /* ---------- existing logic (preserved exactly) ---------- */
  const { t, i18n } = useTranslation();
  const { isTelugu, toggleLanguage } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Theme and text size are app-wide, persisted preferences. They used to be
  // local state here, which meant the header's own toggle desynced from the
  // rest of the app and reset on every reload — the toggle looked broken.
  const { isDark, toggleTheme } = useTheme();
  const { fontSize, setFontSize } = useFontSize();
  const [activeSection, setActiveSection] = useState('home');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { isAuthenticated, logout, user } = useAuth();

  // Header leader photos from API (global, with toggle)
  const [headerLeaders, setHeaderLeaders] = useState(fallbackHeaderLeaders);
  const [showLeaders, setShowLeaders] = useState(true);

  useEffect(() => {
    axios.get('/apiv1/public/portalweb/header-config')
      .then(res => {
        const data = res.data?.data;
        if (data) {
          if (data.show_header_leaders === false) setShowLeaders(false);
          if (Array.isArray(data.leaders) && data.leaders.length > 0) setHeaderLeaders(data.leaders);
        }
      })
      .catch(() => {}); // keep fallback
  }, []);

  // The logged-in UDA's logo (prvdr_ste_lst_t.logo_url_tx, one site per UDA).
  // Stays null for state/DTCP users and for UDAs with no logo uploaded yet — the
  // header then keeps the AP state emblem.
  const [udaLogo, setUdaLogo] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.udaId) { setUdaLogo(null); return; }
    axios.get(`/apiv1/public/portalweb/site-by-uda/${user.udaId}`)
      .then(res => setUdaLogo(res.data?.data?.logo_url_tx || null))
      .catch(() => setUdaLogo(null));
  }, [user?.udaId]);

  // Header identity. This used to read ONLY from the logged-in account
  // (`user.udaName`), which meant the header disagreed with the page whenever the
  // two differed: an anonymous visitor on ruda.localhost got the DTCP wordmark,
  // and a RUDA user browsing amuda.* saw "RUDA" above AMUDA's content.
  // The site actually being viewed wins; the account is the fallback.
  // Outside PortalProvider usePortal() yields the DTCP default, so admin screens
  // that mount this header are unaffected.
  const { siteId } = usePortal();
  const portalUda = getUdaThemeById(siteId);
  const brandCode = portalUda?.code
    || (isAuthenticated && user?.udaName ? (user.udaCode || user.udaName) : null);
  const brandName = portalUda?.fullName
    || (isAuthenticated && user?.udaName ? user.udaName : null);
  // udaLogo is fetched from the logged-in account, so it is only the right mark
  // when the account and the browsed site are the same UDA. Browsing another
  // UDA falls back to the state emblem rather than showing the wrong logo.
  const brandLogo =
    (!portalUda || portalUda.code === user?.udaCode) ? udaLogo : null;

  // portal navbar hover state
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* --- language sync --- */
  useEffect(() => {
    i18n.changeLanguage(isTelugu ? 'te' : 'en');
  }, [isTelugu, i18n]);

  const handleLanguageChange = (lang: 'en' | 'te') => {
    if (lang === 'te' && !isTelugu) toggleLanguage();
    else if (lang === 'en' && isTelugu) toggleLanguage();
    i18n.changeLanguage(lang);
  };

  /* --- scroll detection --- */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* --- intersection observer for active section --- */
  useEffect(() => {
    const ids = ['home', 'about', 'services', 'resources', 'contact'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { root: null, rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => { ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.unobserve(el); }); };
  }, []);

  /* --- font size (delegates to FontSizeProvider, which persists it) --- */
  const FONT_SIZES = ['small', 'medium', 'large'] as const;

  /* --- portal navbar hover handlers --- */
  const clearTimers = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
  }, []);

  const onMenuEnter = useCallback((label: string) => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpenMenu(label), 120);
  }, [clearTimers]);

  const onMenuLeave = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenMenu(null), 160);
  }, [clearTimers]);

  /* --- close dropdowns on outside click / Escape --- */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
        setIsMobileMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpenMenu(null); setIsMobileMenuOpen(false); }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, []);

  /* --- close mobile menu on route change --- */
  useEffect(() => { setIsMobileMenuOpen(false); setOpenMenu(null); }, [location.pathname]);

  /* --- helpers --- */
  const isActive = (link: string) => location.pathname === link;

  /* ---------------------------------------------------------------- */
  /*  RENDER — portal UI design, wired with existing logic            */
  /* ---------------------------------------------------------------- */
  return (
    <>
      {/* Keyboard users reach the content without tabbing the whole nav. */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* ====== 1. TOP UTILITY BAR ====== */}
      {/* A slim government "chrome" strip that lets the saffron band below own
          the colour. It follows the theme: a light stone strip in light mode,
          graphite in dark. It used to be hard-coded graphite, so it stayed
          black whichever theme was selected and made the toggle look dead. */}
      <div className="bg-[#F1EFEC] text-[#4B5263] dark:bg-[#1F2430] dark:text-white/70 border-b border-black/[0.07] dark:border-white/[0.06] hidden md:block relative z-[45]">
        <div className="w-full px-6 lg:px-10 flex items-center justify-between h-[38px]">
          {/* Left — Government of AP mark + social */}
          <div className="flex items-center gap-4">
            <span className="text-[11px] font-medium tracking-[0.12em] uppercase text-[#6B7280] dark:text-white/45 whitespace-nowrap">
              Government of Andhra Pradesh
            </span>
            <span className="w-px h-3.5 bg-black/10 dark:bg-white/15" />
            <div className="flex items-center gap-1">
              <a
                href="https://www.facebook.com/dtcpap"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[#6B7280] hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.05] dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10 transition-all duration-200"
              >
                <i className="ri-facebook-fill text-[15px]" />
              </a>
              <a
                href="https://x.com/dtcpap"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[#6B7280] hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.05] dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10 transition-all duration-200"
              >
                <i className="ri-twitter-x-fill text-[14px]" />
              </a>
            </div>
          </div>

          {/* Right — Text size, UDA name, Language, Theme */}
          <div className="flex items-center gap-3">
            {/* Text-size control. Labelled, because an unlabelled row of three
                "A"s is guesswork for the officer using it. */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#8A8F98] dark:text-white/35">Text size</span>
              <div className="inline-flex bg-black/[0.04] ring-1 ring-black/[0.07] dark:bg-white/[0.07] dark:ring-white/10 rounded-full p-[2px]">
                {FONT_SIZES.map((size) => (
                  <button
                    key={size}
                    onClick={() => setFontSize(size)}
                    aria-label={`Font size ${size}`}
                    aria-pressed={fontSize === size}
                    className={`w-[27px] h-[21px] rounded-full text-[11px] font-semibold leading-none transition-all duration-200 ${
                      fontSize === size
                        ? 'bg-[var(--portal-accent)] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]'
                        : 'text-[#5B6472] hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.06] dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10'
                    }`}
                  >
                    A
                    {size === 'small' && <span className="text-[8px] align-super">−</span>}
                    {size === 'large' && <span className="text-[8px] align-super">+</span>}
                  </button>
                ))}
              </div>
            </div>

            <span className="w-px h-3.5 bg-black/10 dark:bg-white/15" />

            {brandName && (
              <>
                <span className="text-[12px] font-semibold text-[#3B4252] dark:text-white/75 whitespace-nowrap">
                  {brandName}
                </span>
                <span className="w-px h-3.5 bg-black/10 dark:bg-white/15" />
              </>
            )}

            {/* Language pill toggle */}
            <div className="inline-flex bg-black/[0.04] ring-1 ring-black/[0.07] dark:bg-white/[0.07] dark:ring-white/10 rounded-full p-[2px]">
              <button
                onClick={() => handleLanguageChange('en')}
                aria-pressed={!isTelugu}
                className={`px-3 h-[21px] rounded-full text-[11px] font-semibold leading-none transition-all duration-200 ${
                  !isTelugu
                    ? 'bg-[var(--portal-accent)] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]'
                    : 'text-[#5B6472] hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.06] dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10'
                }`}
              >
                English
              </button>
              <button
                onClick={() => handleLanguageChange('te')}
                aria-pressed={isTelugu}
                className={`px-3 h-[21px] rounded-full text-[11px] font-semibold leading-none transition-all duration-200 ${
                  isTelugu
                    ? 'bg-[var(--portal-accent)] text-white shadow-[0_1px_4px_rgba(0,0,0,0.35)]'
                    : 'text-[#5B6472] hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.06] dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10'
                }`}
              >
                తెలుగు
              </button>
            </div>

            {/* Theme toggle — icon pill */}
            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={isDark}
              className="w-[30px] h-[26px] rounded-full bg-black/[0.04] ring-1 ring-black/[0.07] dark:bg-white/[0.07] dark:ring-white/10 flex items-center justify-center text-[#5B6472] dark:text-white/55 transition-all duration-200 hover:text-[color:var(--portal-accent-dark)] hover:bg-black/[0.07] dark:hover:text-white dark:hover:bg-white/15 active:scale-95"
            >
              <i className={`ri-${isDark ? 'sun' : 'moon'}-line text-[13px]`} />
            </button>
          </div>
        </div>
      </div>

      {/* ====== 2. GOVERNMENT IDENTITY BAND ====== */}
      <header className="header-gov-gradient relative overflow-hidden">
        {/* Faint watermark crest at the fold — depth on an otherwise flat band,
            clipped by the header so it never spills onto the nav. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-16 w-64 h-64 rounded-full bg-white/[0.06] blur-2xl"
        />

        <div className="relative z-[1] w-full px-4 sm:px-6 lg:px-10 flex items-center justify-between h-[76px] md:h-[92px]">
          {/* Left — Emblem + Title lockup */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="gov-emblem-plate w-[58px] h-[58px] sm:w-[70px] sm:h-[70px] rounded-2xl flex items-center justify-center flex-shrink-0">
              <img
                src={brandLogo || 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png'}
                alt={brandLogo ? `${user?.udaName} Logo` : 'Andhra Pradesh State Emblem'}
                className="w-[46px] h-[46px] sm:w-[58px] sm:h-[58px] object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.22)]"
                onError={(e) => { (e.target as HTMLImageElement).src = 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png'; }}
              />
            </div>
            <div className="min-w-0">
              <h1 className="!text-white text-[16px] sm:text-[19px] lg:text-[22px] font-bold leading-tight tracking-[-0.01em] truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.18)]">
                {brandCode || t('header.dtcp')}
              </h1>
              <p className="!text-white/80 text-[11px] sm:text-[12.5px] font-medium leading-tight mt-[3px] truncate">
                {brandName || t('header.department')}
              </p>
            </div>
          </div>

          {/* Right — Leader photos (desktop only, respects global toggle).
              Each sits on its own soft glass card so the portraits read as a
              deliberate pair rather than two cut-outs floating on the band. */}
          {showLeaders && (
          <div className="hidden lg:flex items-center gap-2.5">
            {headerLeaders.map((leader, i) => (
              <div
                key={i}
                className="group flex items-center gap-2.5 rounded-full bg-white/[0.10] ring-1 ring-white/20 pl-1.5 pr-4 py-1.5 transition-all duration-300 hover:bg-white/[0.18]"
              >
                <img
                  src={leader.pht_url_tx}
                  alt={leader.ldr_nm_tx}
                  className="w-11 h-11 rounded-full object-cover ring-2 ring-white/55 transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                />
                <div className="leading-tight">
                  <p className="!text-white text-[12.5px] font-semibold whitespace-nowrap">{leader.ldr_nm_tx}</p>
                  <p className="!text-white/70 text-[10.5px] whitespace-nowrap">{leader.dsgntn_tx}</p>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </header>

      {/* ====== 3. NAVBAR ====== */}
      <nav
        ref={navRef}
        className={`w-full z-40 nav-glass border-b border-black/[0.06] dark:border-white/[0.08] transition-shadow duration-300 ${
          isScrolled ? 'fixed top-0 left-0 right-0 shadow-[0_6px_24px_-8px_rgba(0,0,0,0.22)]' : 'relative'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="w-full px-4 sm:px-6 lg:px-10">
          {/* Desktop Nav Items */}
          <div className="hidden lg:flex items-center h-[52px] gap-0.5">
            {isScrolled && (
              <Link to="/" className="flex items-center gap-2 mr-3 pr-3 border-r border-black/10 dark:border-white/15">
                <img
                  src={brandLogo || 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png'}
                  alt={brandLogo ? `${user?.udaName} Logo` : 'AP Emblem'}
                  className="w-8 h-8 object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).src = 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png'; }}
                />
              </Link>
            )}
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.link) || (item.link !== '/' && location.pathname.startsWith(item.link));
              const hasChildren = !!item.children?.length;
              const lit = active || openMenu === item.label;

              return (
                <div
                  key={item.label}
                  className="relative h-full flex items-center"
                  onMouseEnter={() => hasChildren && onMenuEnter(item.label)}
                  onMouseLeave={() => hasChildren && onMenuLeave()}
                >
                  {/* Pill treatment rather than the old full-height white block
                      with a hard 3px underline — the block read as a tab torn
                      out of the bar. The accent rule below is now a short,
                      rounded marker that grows from the centre. */}
                  <Link
                    to={item.link}
                    aria-haspopup={hasChildren ? 'true' : undefined}
                    aria-expanded={openMenu === item.label ? 'true' : 'false'}
                    aria-current={active ? 'page' : undefined}
                    className={`
                      group relative flex items-center gap-1 px-3.5 py-2 rounded-lg
                      text-[13.5px] font-semibold tracking-[-0.005em]
                      whitespace-nowrap cursor-pointer
                      transition-all duration-200 ease-out
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--portal-accent)] focus-visible:ring-offset-1
                      ${lit
                        ? 'text-[color:var(--portal-accent-dark)] dark:text-[color:var(--portal-accent-light)] bg-[var(--portal-accent-soft)] dark:bg-white/[0.08]'
                        : 'text-[#3B4252] dark:text-white/75 hover:text-[color:var(--portal-accent-dark)] dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                      }
                    `}
                  >
                    {item.label}
                    {hasChildren && (
                      <i className={`ri-arrow-down-s-line text-[13px] opacity-55 transition-transform duration-200 ${openMenu === item.label ? 'rotate-180' : ''}`} />
                    )}
                    {/* Active marker */}
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-[-9px] h-[3px] rounded-full bg-[var(--portal-accent)] transition-all duration-300 ${
                        active ? 'w-5 opacity-100' : 'w-0 opacity-0'
                      }`}
                    />
                  </Link>

                  {/* Dropdown */}
                  {hasChildren && openMenu === item.label && (
                    <div
                      role="menu"
                      className="absolute top-full left-0 mt-2 z-50 bg-white dark:bg-[#1c1e26] rounded-xl border border-black/[0.07] dark:border-white/10 overflow-hidden dropdown-panel"
                      style={{
                        minWidth: '236px',
                        maxWidth: '300px',
                        boxShadow: '0 18px 44px -12px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.06)',
                      }}
                      onMouseEnter={() => { clearTimers(); setOpenMenu(item.label); }}
                      onMouseLeave={onMenuLeave}
                    >
                      {/* Accent cap ties the panel to the item that opened it */}
                      <div className="h-[3px] w-full bg-gradient-to-r from-[var(--portal-accent)] to-[var(--portal-accent-light)]" />
                      <ul className="py-2">
                        {item.children!.map((child) => (
                          <li key={child.link}>
                            <Link
                              to={child.link}
                              role="menuitem"
                              className={`
                                group/child flex items-center justify-between gap-3 mx-2 px-3 py-2.5 rounded-lg
                                text-[13px] font-medium leading-snug
                                transition-all duration-150 cursor-pointer
                                focus:outline-none focus-visible:bg-[var(--portal-accent-soft)]
                                ${isActive(child.link)
                                  ? 'text-[color:var(--portal-accent-dark)] bg-[var(--portal-accent-soft)] dark:bg-white/[0.08] dark:text-[color:var(--portal-accent-light)]'
                                  : 'text-[#3B4252] dark:text-white/75 hover:bg-[var(--portal-accent-soft)] dark:hover:bg-white/[0.07] hover:text-[color:var(--portal-accent-dark)] dark:hover:text-white'
                                }
                              `}
                            >
                              {child.label}
                              <i className="ri-arrow-right-s-line text-[14px] opacity-0 -translate-x-1 transition-all duration-150 group-hover/child:opacity-60 group-hover/child:translate-x-0" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Right — Login / Auth Button */}
            <div className="ml-auto flex items-center gap-2 pl-3">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => redirectToUserMainPage()}
                    className="group inline-flex items-center gap-1.5 bg-gradient-to-b from-[var(--portal-accent)] to-[var(--portal-accent-dark)] text-white px-4 py-[7px] text-[13px] font-semibold rounded-lg shadow-[0_4px_14px_-4px_var(--portal-accent)] hover:shadow-[0_8px_20px_-6px_var(--portal-accent)] hover:-translate-y-px active:translate-y-0 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-dashboard-line text-[14px]" />
                    {t('header.gotoERP')}
                    <i className="ri-arrow-right-line text-[13px] transition-transform duration-200 group-hover:translate-x-0.5" />
                  </button>
                  <button
                    onClick={() => logout()}
                    className="inline-flex items-center gap-1.5 border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/[0.06] text-[#4B5263] dark:text-white/75 px-3.5 py-[7px] text-[13px] font-semibold rounded-lg hover:text-[color:var(--portal-accent-dark)] hover:border-[color:var(--portal-accent)] hover:bg-[var(--portal-accent-soft)] transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-logout-box-r-line text-[14px]" />
                    {t('header.logout')}
                  </button>
                </>
              ) : (
                <>
                  <a
                    href="/app/vendor-portal/login"
                    className="inline-flex items-center gap-1.5 border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/[0.06] text-[#4B5263] dark:text-white/75 px-3.5 py-[7px] text-[13px] font-semibold rounded-lg hover:text-[color:var(--portal-accent-dark)] hover:border-[color:var(--portal-accent)] hover:bg-[var(--portal-accent-soft)] transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-store-2-line text-[14px]" />
                    Vendor Login
                  </a>
                  <button
                    onClick={() => setIsLoginModalOpen(true)}
                    className="group inline-flex items-center gap-1.5 bg-gradient-to-b from-[var(--portal-accent)] to-[var(--portal-accent-dark)] text-white px-4 py-[7px] text-[13px] font-semibold rounded-lg shadow-[0_4px_14px_-4px_var(--portal-accent)] hover:shadow-[0_8px_20px_-6px_var(--portal-accent)] hover:-translate-y-px active:translate-y-0 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-login-box-line text-[14px]" />
                    {t('header.login')}
                    <i className="ri-arrow-right-line text-[13px] transition-transform duration-200 group-hover:translate-x-0.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── MOBILE TOGGLE ── */}
          <div className="lg:hidden flex items-center justify-between h-[52px]">
            <span className="text-[#222222] dark:text-white font-bold text-sm uppercase tracking-wider">
              DTCP Andhra Pradesh
            </span>
            <div className="flex items-center gap-2">
              {!isAuthenticated && (
                <>
                  <a
                    href="/app/vendor-portal/login"
                    className="px-3 py-1 border border-[color:var(--portal-accent)] text-[color:var(--portal-accent)] text-xs font-semibold rounded-md"
                  >
                    Vendor
                  </a>
                  <button
                    onClick={() => setIsLoginModalOpen(true)}
                    className="px-3 py-1 bg-[var(--portal-accent)] text-white text-xs font-semibold rounded-md"
                  >
                    {t('header.login')}
                  </button>
                </>
              )}
              {isAuthenticated && (
                <button
                  onClick={() => redirectToUserMainPage()}
                  className="px-3 py-1 bg-[var(--portal-accent)] text-white text-xs font-semibold rounded-md"
                >
                  ERP
                </button>
              )}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-[color:var(--portal-accent)] p-2 hover:bg-white cursor-pointer transition-colors rounded-md focus:outline-none"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`} />
              </button>
            </div>
          </div>
        </div>

        {/* ====== MOBILE MENU OVERLAY ====== */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-[52px] z-50 bg-[#161A22] overflow-y-auto"
            role="dialog"
            aria-label="Mobile navigation"
          >
            <div className="px-4 py-2 border-b border-white/10 flex items-center justify-between">
              <span className="text-[10px] text-white/40 uppercase tracking-widest">Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white/50 hover:text-white cursor-pointer text-xs"
              >
                Close ✕
              </button>
            </div>

            {NAV_ITEMS.map((item, idx) => {
              const hasChildren = !!item.children?.length;
              const expanded = mobileExpanded === item.label;

              return (
                <div key={item.label} className="border-b border-white/10">
                  <div
                    className={`
                      flex items-center justify-between px-4 py-3 cursor-pointer transition-colors
                      ${expanded
                        ? 'bg-[var(--portal-accent)] text-white'
                        : 'text-white hover:bg-[color-mix(in_srgb,var(--portal-accent)_22%,transparent)]'
                      }
                    `}
                    onClick={() => {
                      if (hasChildren) {
                        setMobileExpanded(expanded ? null : item.label);
                      } else {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                    role={hasChildren ? 'button' : undefined}
                    aria-expanded={hasChildren ? expanded : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-white/30 font-mono w-4">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[13px] font-semibold">{item.label}</span>
                    </div>
                    {hasChildren && (
                      <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-base opacity-60`} />
                    )}
                  </div>

                  {hasChildren && expanded && (
                    <div className="bg-[#1a1a1a]">
                      {item.children!.map((child) => (
                        <Link
                          key={child.link}
                          to={child.link}
                          className="
                            block px-10 py-2.5
                            text-white/70 hover:text-white hover:bg-[color-mix(in_srgb,var(--portal-accent)_25%,transparent)]
                            border-l-4 border-transparent hover:border-[color:var(--portal-accent)]
                            text-[12px] font-medium transition-colors cursor-pointer
                          "
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Mobile Auth Buttons */}
            <div className="px-4 py-4 mt-2 flex gap-3">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => { redirectToUserMainPage(); setIsMobileMenuOpen(false); }}
                    className="flex-1 bg-[var(--portal-accent)] text-white text-center py-2.5 text-[12px] font-semibold rounded-md"
                  >
                    {t('header.gotoERP')}
                  </button>
                  <button
                    onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                    className="flex-1 border border-white/20 text-white text-center py-2.5 text-[12px] font-semibold rounded-md"
                  >
                    {t('header.logout')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setIsLoginModalOpen(true); setIsMobileMenuOpen(false); }}
                  className="flex-1 bg-[var(--portal-accent)] text-white text-center py-2.5 text-[12px] font-semibold rounded-md"
                >
                  {t('header.login')}
                </button>
              )}
            </div>

            {/* Mobile Utility Controls */}
            <div className="px-4 pb-4 pt-3 border-t border-white/10">
              <div className="flex items-center justify-between gap-3">
                {/* Font Size */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white/40 uppercase tracking-wide">Size</span>
                  <div className="inline-flex bg-white/10 rounded-full p-[2px]">
                    {FONT_SIZES.map((size) => (
                      <button
                        key={size}
                        onClick={() => setFontSize(size)}
                        className={`w-[26px] h-[22px] rounded-full text-[11px] font-semibold transition-all duration-200 ${
                          fontSize === size
                            ? 'bg-[var(--portal-accent)] text-white shadow-[0_1px_3px_rgba(230,106,26,0.4)]'
                            : 'text-white/50 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        A
                        {size === 'small' && <span className="text-[8px] align-super">-</span>}
                        {size === 'large' && <span className="text-[8px] align-super">+</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language */}
                <div className="inline-flex bg-white/10 rounded-full p-[2px]">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`px-3 h-[22px] rounded-full text-[11px] font-medium transition-all duration-200 ${
                      !isTelugu ? 'bg-[var(--portal-accent)] text-white' : 'text-white/50 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('te')}
                    className={`px-3 h-[22px] rounded-full text-[11px] font-medium transition-all duration-200 ${
                      isTelugu ? 'bg-[var(--portal-accent)] text-white' : 'text-white/50 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    తె
                  </button>
                </div>

                {/* Theme */}
                <button
                  onClick={toggleTheme}
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDark}
                  className="w-[30px] h-[22px] rounded-full bg-white/10 flex items-center justify-center transition-all duration-200 hover:bg-[color-mix(in_srgb,var(--portal-accent)_22%,transparent)] active:scale-95"
                >
                  <i className={`ri-${isDark ? 'sun' : 'moon'}-line text-[13px] text-white/50`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer when navbar is fixed */}
      {isScrolled && <div className="h-[52px]" />}

      {/* ====== LOGIN MODAL (existing) ====== */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
};
